import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import { ApiError } from '@/api';
import { useNotify } from '@/components/feedback/notificationsContext';
import { useAsync } from '@/hooks/useAsync';
import {
  createCourse,
  createInstitution,
  listCourses,
  listInstitutions,
  updateCourse,
  updateInstitution,
} from '@/platform/api';
import { StatusChip } from '@/platform/billing/BillingPanel';
import type { AccessStatus } from '@/platform/billing/types';
import { useSession } from '@/platform/session';
import type { AllowedEmail, Course, EmailRule, Institution } from '@/platform/types';
import { PageHeader } from '../PageHeader';
import { AdminTabs } from './AdminPage';

const ROLE_WORD = { student: 'Estudantes', teacher: 'Professores' } as const;

/** Admins: the institutions (with their institutional address formats) and the courses. */
export function InstitutionsPage() {
  const institutions = useAsync(listInstitutions);
  const courses = useAsync((signal) => listCourses(true, signal));
  const { refresh } = useSession();
  const [editing, setEditing] = useState<Institution | 'new' | null>(null);
  const [course, setCourse] = useState<Course | 'new' | null>(null);

  return (
    <>
      <PageHeader
        title="Administração"
        subtitle="Instituições, os e-mails institucionais que cada uma aceita e os cursos."
      />
      <AdminTabs />
      <Stack spacing={4}>
        <Box>
          <Stack direction="row" sx={{ alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
              Instituições
            </Typography>
            <Button startIcon={<AddRounded />} onClick={() => setEditing('new')}>
              Nova instituição
            </Button>
          </Stack>
          {institutions.error ? (
            <Alert severity="error">Não foi possível carregar as instituições.</Alert>
          ) : institutions.loading ? (
            <Skeleton variant="rounded" height={160} />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              {(institutions.data ?? []).map((i) => (
                <Card key={i.id} sx={{ opacity: i.isActive ? 1 : 0.6 }}>
                  <CardContent>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
                        {i.name}
                      </Typography>
                      {!i.isActive && <Chip size="small" label="Inativa" />}
                      {i.isActive && i.accessStatus && (
                        <StatusChip status={i.accessStatus as AccessStatus} />
                      )}
                      <IconButton aria-label={`Editar ${i.name}`} onClick={() => setEditing(i)}>
                        <EditRounded fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {i.students} estudante(s) · {i.teachers} professor(es) · {i.coordinators} na
                      coordenação
                    </Typography>
                    <Stack spacing={0.5}>
                      {i.emailRules.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Nenhum e-mail institucional aceito ainda.
                        </Typography>
                      ) : (
                        i.emailRules.map((r, n) => (
                          <Typography key={n} variant="body2">
                            <b>{ROLE_WORD[r.role]}:</b> {r.example || `…@${r.domain}`}
                            {r.registration && ' (RA)'}
                          </Typography>
                        ))
                      )}
                      {i.allowedEmails.length > 0 && (
                        <Typography variant="body2">
                          <b>Liberados individualmente:</b>{' '}
                          {i.allowedEmails.map((e) => e.email).join(', ')}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        <Box>
          <Stack direction="row" sx={{ alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
              Cursos
            </Typography>
            <Button startIcon={<AddRounded />} onClick={() => setCourse('new')}>
              Novo curso
            </Button>
          </Stack>
          {courses.error ? (
            <Alert severity="error">Não foi possível carregar os cursos.</Alert>
          ) : (
            <Card>
              {courses.loading
                ? [0, 1, 2].map((n) => (
                    <Skeleton key={n} variant="rounded" height={40} sx={{ m: 2 }} />
                  ))
                : (courses.data ?? []).map((c) => (
                    <Stack
                      key={c.id}
                      direction="row"
                      spacing={2}
                      sx={(t) => ({
                        px: 2.5,
                        py: 1.25,
                        alignItems: 'center',
                        opacity: c.isActive ? 1 : 0.6,
                        '& + &': { borderTop: `1px solid ${t.vars.palette.divider}` },
                      })}
                    >
                      <Typography sx={{ flex: 1 }}>
                        {c.name}
                        {!c.isActive && ' (inativo)'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {c.students} estudante(s)
                      </Typography>
                      <IconButton aria-label={`Editar ${c.name}`} onClick={() => setCourse(c)}>
                        <EditRounded fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
            </Card>
          )}
        </Box>
      </Stack>

      <InstitutionDialog
        target={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          institutions.reload();
          void refresh(); // the top-bar list
        }}
      />
      <CourseDialog
        target={course}
        onClose={() => setCourse(null)}
        onSaved={() => {
          setCourse(null);
          courses.reload();
        }}
      />
    </>
  );
}

const EMPTY_RULE: EmailRule = {
  role: 'student',
  domain: '',
  pattern: '^[a-z0-9._%+-]+$',
  example: '',
  registration: false,
};

function InstitutionDialog({
  target,
  onClose,
  onSaved,
}: {
  target: Institution | 'new' | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const notify = useNotify();
  const existing = target && target !== 'new' ? target : null;
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [rules, setRules] = useState<EmailRule[]>([]);
  const [allowed, setAllowed] = useState<AllowedEmail[]>([]);
  const [courtesy, setCourtesy] = useState(true);
  const [last, setLast] = useState<typeof target>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (target !== last) {
    setLast(target);
    setError(null);
    setName(existing?.name ?? '');
    setIsActive(existing?.isActive ?? true);
    setRules(existing?.emailRules ?? [{ ...EMPTY_RULE }, { ...EMPTY_RULE, role: 'teacher' }]);
    setAllowed(existing?.allowedEmails ?? []);
    setCourtesy(true);
  }

  const setRule = (index: number, patch: Partial<EmailRule>) =>
    setRules((list) => list.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const setEntry = (index: number, patch: Partial<AllowedEmail>) =>
    setAllowed((list) => list.map((e, i) => (i === index ? { ...e, ...patch } : e)));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const input = {
        name: name.trim(),
        isActive,
        emailRules: rules.filter((r) => r.domain.trim()),
        allowedEmails: allowed.filter((e) => e.email.trim()),
        courtesy,
      };
      if (existing) await updateInstitution(existing.id, input);
      else await createInstitution(input);
      notify(existing ? 'Instituição atualizada.' : 'Instituição cadastrada.', 'success');
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={Boolean(target)}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      slotProps={{ paper: { component: 'form', onSubmit: submit, noValidate: true } as object }}
    >
      <DialogTitle>{existing ? `Editar ${existing.name}` : 'Nova instituição'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              E-mails institucionais aceitos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No cadastro, a pessoa confirma um e-mail neste formato. O formato é uma expressão
              regular para a parte antes do @: <code>^[0-9]+$</code> aceita só números (um RA),{' '}
              <code>^prof\..+$</code> exige começar com “prof.” e <code>^[a-z0-9._%+-]+$</code>{' '}
              aceita qualquer nome.
            </Typography>
            <Stack spacing={2}>
              {rules.map((rule, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '140px 1fr 1fr',
                      md: '140px 1fr 1fr 1.2fr auto auto',
                    },
                    gap: 1.5,
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    select
                    size="small"
                    label="Para"
                    value={rule.role}
                    onChange={(e) => setRule(index, { role: e.target.value as EmailRule['role'] })}
                  >
                    <MenuItem value="student">Estudantes</MenuItem>
                    <MenuItem value="teacher">Professores</MenuItem>
                  </TextField>
                  <TextField
                    size="small"
                    label="Domínio"
                    placeholder="maua.br"
                    value={rule.domain}
                    onChange={(e) => setRule(index, { domain: e.target.value })}
                  />
                  <TextField
                    size="small"
                    label="Formato"
                    value={rule.pattern}
                    onChange={(e) => setRule(index, { pattern: e.target.value })}
                    slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
                  />
                  <TextField
                    size="small"
                    label="Exemplo"
                    placeholder="12.00000-0@maua.br"
                    value={rule.example}
                    onChange={(e) => setRule(index, { example: e.target.value })}
                  />
                  <Tooltip title="A parte antes do @ é o RA do estudante">
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={rule.registration}
                          disabled={rule.role !== 'student'}
                          onChange={(e) => setRule(index, { registration: e.target.checked })}
                        />
                      }
                      label="RA"
                    />
                  </Tooltip>
                  <IconButton
                    aria-label="Remover formato"
                    onClick={() => setRules((list) => list.filter((_, i) => i !== index))}
                  >
                    <DeleteOutlineRounded fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Box>
                <Button
                  size="small"
                  startIcon={<AddRounded />}
                  onClick={() => setRules((list) => [...list, { ...EMPTY_RULE }])}
                >
                  Adicionar formato
                </Button>
              </Box>
            </Stack>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              E-mails liberados individualmente
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Para quem não tem o e-mail da instituição: a pessoa informa este endereço no cadastro
              e entra sem precisar confirmá-lo. Ele não aparece para mais ninguém; remova quando não
              for mais necessário (quem já entrou continua com acesso).
            </Typography>
            <Stack spacing={2}>
              {allowed.map((entry, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '140px 1fr auto' },
                    gap: 1.5,
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    select
                    size="small"
                    label="Como"
                    value={entry.role}
                    onChange={(e) =>
                      setEntry(index, { role: e.target.value as AllowedEmail['role'] })
                    }
                  >
                    <MenuItem value="student">Estudante</MenuItem>
                    <MenuItem value="teacher">Professor(a)</MenuItem>
                  </TextField>
                  <TextField
                    size="small"
                    type="email"
                    label="E-mail"
                    placeholder="nome@outlook.com.br"
                    value={entry.email}
                    onChange={(e) => setEntry(index, { email: e.target.value })}
                  />
                  <IconButton
                    aria-label={`Remover ${entry.email || 'e-mail'}`}
                    onClick={() => setAllowed((list) => list.filter((_, i) => i !== index))}
                  >
                    <DeleteOutlineRounded fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Box>
                <Button
                  size="small"
                  startIcon={<AddRounded />}
                  onClick={() => setAllowed((list) => [...list, { email: '', role: 'student' }])}
                >
                  Liberar e-mail
                </Button>
              </Box>
            </Stack>
          </Box>
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label="Ativa (aparece no cadastro)"
          />
          {!existing && (
            <FormControlLabel
              control={
                <Switch checked={courtesy} onChange={(e) => setCourtesy(e.target.checked)} />
              }
              label={
                courtesy
                  ? 'Liberar por cortesia (sem cobrança; dá para encerrar em Assinaturas)'
                  : 'Sem acesso até a coordenação contratar um plano'
              }
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" disabled={saving || !name.trim()}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CourseDialog({
  target,
  onClose,
  onSaved,
}: {
  target: Course | 'new' | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const notify = useNotify();
  const existing = target && target !== 'new' ? target : null;
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [last, setLast] = useState<typeof target>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (target !== last) {
    setLast(target);
    setError(null);
    setName(existing?.name ?? '');
    setIsActive(existing?.isActive ?? true);
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const input = { name: name.trim(), isActive };
      if (existing) await updateCourse(existing.id, input);
      else await createCourse(input);
      notify(existing ? 'Curso atualizado.' : 'Curso cadastrado.', 'success');
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={Boolean(target)}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { component: 'form', onSubmit: submit, noValidate: true } as object }}
    >
      <DialogTitle>{existing ? 'Editar curso' : 'Novo curso'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label="Ativo (aparece no cadastro)"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" disabled={saving || name.trim().length < 2}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
